import { useState, useEffect, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useSchool } from '../context/SchoolContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Building2, Plus, MapPin, LogOut, ChevronLeft, ChevronRight, X, Wallet, FolderOpen, Sparkles, Crown, Clock, Pencil } from 'lucide-react';
import { toast } from 'sonner';
import { format, startOfWeek, addDays, addWeeks, subWeeks, isToday, isSameDay } from 'date-fns';
import { API_BASE } from '../apiConfig';

const API = API_BASE;

const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const DAY_COLORS = [
  'bg-blue-50 border-blue-200',
  'bg-green-50 border-green-200',
  'bg-yellow-50 border-yellow-200',
  'bg-purple-50 border-purple-200',
  'bg-cyan-50 border-cyan-200',
  'bg-orange-50 border-orange-200',
  'bg-pink-50 border-pink-200',
];

export const SchoolSelectPage = () => {
  const { user, logout } = useAuth();
  const { selectSchool } = useSchool();
  const navigate = useNavigate();
  const [schools, setSchools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newSchool, setNewSchool] = useState({ name: '', address: '', periods_per_day: 6 });
  const [creating, setCreating] = useState(false);
  const [trialStatus, setTrialStatus] = useState(null);
  const [editingSchool, setEditingSchool] = useState(null);
  const [editSchoolName, setEditSchoolName] = useState('');
  const [editSchoolAddress, setEditSchoolAddress] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);

  // Weekly planner state
  const [weekOffset, setWeekOffset] = useState(0);
  const [plannerEntries, setPlannerEntries] = useState({});

  const weekStart = useMemo(() => {
    const now = new Date();
    const monday = startOfWeek(now, { weekStartsOn: 1 });
    return addWeeks(monday, weekOffset);
  }, [weekOffset]);

  const weekDays = useMemo(() => {
    return Array.from({ length: 5 }, (_, i) => addDays(weekStart, i));
  }, [weekStart]);

  const fetchSchools = async () => {
    try {
      const response = await fetch(`${API}/schools`, { credentials: 'include' });
      if (response.ok) setSchools(await response.json());
    } catch (error) { console.error('Failed to fetch schools:', error); }
    finally { setLoading(false); }
  };

  const fetchTrialStatus = async () => {
    try {
      const response = await fetch(`${API}/auth/trial-status`, { credentials: 'include' });
      if (response.ok) setTrialStatus(await response.json());
    } catch (error) { console.error('Failed to fetch trial status:', error); }
  };

  const fetchPlanner = async () => {
    try {
      const ws = format(weekStart, 'yyyy-MM-dd');
      const response = await fetch(`${API}/weekly-planner?week_start=${ws}`, { credentials: 'include' });
      if (response.ok) {
        const entries = await response.json();
        const map = {};
        entries.forEach(e => { map[e.date] = e; });
        setPlannerEntries(map);
      }
    } catch (error) { console.error('Failed to fetch planner:', error); }
  };

  useEffect(() => { fetchSchools(); fetchTrialStatus(); }, []);
  useEffect(() => { fetchPlanner(); }, [weekStart]);

  const handleSetPlannerDay = async (date, schoolId) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    if (!schoolId || schoolId === 'clear') {
      try {
        await fetch(`${API}/weekly-planner/${dateStr}`, { method: 'DELETE', credentials: 'include' });
        setPlannerEntries(prev => { const copy = { ...prev }; delete copy[dateStr]; return copy; });
      } catch (error) { console.error('Failed to clear planner:', error); }
      return;
    }
    const school = schools.find(s => s.school_id === schoolId);
    if (!school) return;
    try {
      await fetch(`${API}/weekly-planner/${dateStr}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ school_id: schoolId, school_name: school.name })
      });
      setPlannerEntries(prev => ({
        ...prev, [dateStr]: { school_id: schoolId, school_name: school.name, date: dateStr }
      }));
    } catch (error) { console.error('Failed to set planner:', error); }
  };

  const handleCreateSchool = async () => {
    if (!newSchool.name.trim()) { toast.error('School name is required'); return; }
    setCreating(true);
    try {
      const response = await fetch(`${API}/schools`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify(newSchool)
      });
      if (response.ok) { toast.success('School added'); setDialogOpen(false); setNewSchool({ name: '', address: '', periods_per_day: 6 }); fetchSchools(); }
      else toast.error('Failed to create school');
    } catch (error) { toast.error('Failed to create school'); }
    finally { setCreating(false); }
  };

  const handleSelectSchool = (school) => {
    selectSchool(school);
    toast.success(`Selected ${school.name}`);
    navigate('/app/daily-overview');
  };

  const handleLogout = async () => { await logout(); navigate('/'); };

  const handleRenameSchool = async () => {
    if (!editSchoolName.trim()) { toast.error('School name is required'); return; }
    setSavingEdit(true);
    try {
      const response = await fetch(`${API}/schools/${editingSchool.school_id}/rename`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ name: editSchoolName.trim(), address: editSchoolAddress.trim() })
      });
      if (response.ok) {
        toast.success('School updated');
        setEditingSchool(null);
        fetchSchools();
      } else toast.error('Failed to update school');
    } catch (error) { toast.error('Failed to update school'); }
    finally { setSavingEdit(false); }
  };

  return (
    <div className="min-h-screen bg-google-grey-50" data-testid="school-select-page">
      {/* Header */}
      <header className="bg-white border-b border-google-grey-200">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/logo.png" alt="Reliefify" className="w-14 h-14 object-contain" />
          </div>
          <Button variant="ghost" onClick={handleLogout} className="text-google-grey-600 hover:text-google-grey-800 hover:bg-google-grey-100" data-testid="logout-btn">
            <LogOut className="w-4 h-4 mr-2" />Sign out
          </Button>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-10">
        {/* Trial Welcome Banner */}
        {trialStatus && !trialStatus.is_pro && trialStatus.trial_active && (
          <div className="mb-8 rounded-2xl bg-gradient-to-r from-teal-600 via-emerald-500 to-teal-600 p-5 text-white shadow-lg" data-testid="trial-welcome-banner">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center flex-shrink-0">
                  <Sparkles className="w-6 h-6" />
                </div>
                <div>
                  <p className="font-bold text-lg">Welcome to your free trial!</p>
                  <p className="text-sm text-white/90">{trialStatus.days_left} day{trialStatus.days_left !== 1 ? 's' : ''} remaining — try every feature, no limits</p>
                </div>
              </div>
              <Link to="/app/upgrade">
                <Button size="sm" className="bg-white text-teal-700 hover:bg-teal-50 rounded-full px-5 h-9 text-sm font-bold shadow-md hover:shadow-lg transition-all hover:scale-105 whitespace-nowrap">
                  <Crown className="w-4 h-4 mr-1.5" />
                  Upgrade — $4.99/mo
                </Button>
              </Link>
            </div>
          </div>
        )}
        {trialStatus && !trialStatus.is_pro && !trialStatus.trial_active && (
          <div className="mb-8 rounded-2xl bg-gradient-to-r from-rose-600 via-orange-500 to-amber-500 p-5 text-white shadow-lg" data-testid="trial-expired-banner">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center flex-shrink-0">
                  <Clock className="w-6 h-6" />
                </div>
                <div>
                  <p className="font-bold text-lg">Your trial has ended</p>
                  <p className="text-sm text-white/90">Subscribe now to keep using Reliefify</p>
                </div>
              </div>
              <Link to="/app/upgrade">
                <Button size="sm" className="bg-white text-orange-700 hover:bg-orange-50 rounded-full px-5 h-9 text-sm font-bold shadow-md hover:shadow-lg transition-all hover:scale-105 animate-pulse whitespace-nowrap">
                  <Crown className="w-4 h-4 mr-1.5" />
                  Subscribe — $4.99/mo
                </Button>
              </Link>
            </div>
          </div>
        )}

        <div className="text-center mb-8">
          <h1 className="text-3xl font-heading font-medium text-google-grey-900 mb-2">
            Welcome, {user?.name?.split(' ')[0]}
          </h1>
          <p className="text-lg text-google-grey-600">Select or add a school to get started</p>
        </div>

        {/* Add school button */}
        <div className="flex justify-center mb-8">
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-google-blue hover:bg-blue-600 text-white rounded-md h-11 px-6 shadow-google hover:shadow-google-hover transition-shadow" data-testid="add-school-btn">
                <Plus className="w-5 h-5 mr-2" />Add School
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-white">
              <DialogHeader><DialogTitle className="font-heading text-xl text-google-grey-900">Add School</DialogTitle></DialogHeader>
              <div className="space-y-4 pt-4">
                <div>
                  <label className="text-sm font-medium text-google-grey-700">School Name</label>
                  <Input placeholder="Enter school name" value={newSchool.name} onChange={(e) => setNewSchool({ ...newSchool, name: e.target.value })} className="mt-1 border-google-grey-300" data-testid="school-name-input" />
                </div>
                <div>
                  <label className="text-sm font-medium text-google-grey-700">Address (optional)</label>
                  <Input placeholder="Enter school address" value={newSchool.address} onChange={(e) => setNewSchool({ ...newSchool, address: e.target.value })} className="mt-1 border-google-grey-300" data-testid="school-address-input" />
                </div>
                <div>
                  <label className="text-sm font-medium text-google-grey-700">Periods per day</label>
                  <Input type="number" min="1" max="12" value={newSchool.periods_per_day} onChange={(e) => {
                    const raw = e.target.value;
                    if (raw === '') {
                      setNewSchool({ ...newSchool, periods_per_day: '' });
                      return;
                    }
                    const num = parseInt(raw);
                    if (!isNaN(num)) {
                      setNewSchool({ ...newSchool, periods_per_day: Math.min(12, Math.max(1, num)) });
                    }
                  }} onBlur={() => {
                    if (newSchool.periods_per_day === '' || isNaN(newSchool.periods_per_day)) {
                      setNewSchool({ ...newSchool, periods_per_day: 6 });
                    }
                  }} className="mt-1 border-google-grey-300" data-testid="periods-input" />
                  <p className="text-xs text-google-grey-400 mt-1">Used to build your timetable grid</p>
                </div>
                <Button onClick={handleCreateSchool} disabled={creating} className="w-full bg-google-blue hover:bg-blue-600 text-white rounded-md h-11" data-testid="create-school-btn">
                  {creating ? 'Adding...' : 'Add School'}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {/* Schools list */}
        {loading ? (
          <div className="text-center py-8 text-google-grey-500">Loading your schools...</div>
        ) : schools.length === 0 ? (
          <Card className="bg-white border border-dashed border-google-grey-300 shadow-none mb-10">
            <CardContent className="py-12 text-center">
              <Building2 className="w-16 h-16 text-google-grey-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-google-grey-800 mb-2">No schools yet</h3>
              <p className="text-google-grey-500">Add your first school to get started</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3 mb-10">
            {schools.map((school, index) => (
              <Card key={school.school_id}
                className="bg-white border border-google-grey-200 hover:shadow-google-hover transition-all duration-200 cursor-pointer animate-fade-in"
                style={{ animationDelay: `${index * 50}ms` }}
                data-testid={`school-card-${school.school_id}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-center gap-4">
                    <div className={`w-12 h-12 rounded-lg class-color-${(index % 6) + 1} flex items-center justify-center flex-shrink-0`}
                      onClick={() => handleSelectSchool(school)}
                    >
                      <Building2 className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-1 min-w-0" onClick={() => handleSelectSchool(school)}>
                      <h3 className="text-base font-medium text-google-grey-900">{school.name}</h3>
                      {school.address && (
                        <p className="text-sm text-google-grey-500 flex items-center gap-1 mt-0.5">
                          <MapPin className="w-3 h-3 flex-shrink-0" /><span className="truncate">{school.address}</span>
                        </p>
                      )}
                    </div>
                    <button
                      onClick={(e) => { e.stopPropagation(); setEditingSchool(school); setEditSchoolName(school.name); setEditSchoolAddress(school.address || ''); }}
                      className="p-2 rounded-lg hover:bg-slate-100 transition-colors text-slate-400 hover:text-teal-600 flex-shrink-0"
                      data-testid={`edit-school-${school.school_id}`}
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* My Stuff — quick access */}
        <div className="mb-10">
          <h2 className="text-sm font-heading font-semibold text-slate-400 uppercase tracking-wider mb-3">The Essentials</h2>
          <div className="grid grid-cols-2 gap-3">
            <Link to="/app/earnings" data-testid="quick-earnings">
              <Card className="bg-white border border-slate-200 hover:border-teal-200 hover:shadow-md transition-all cursor-pointer">
                <CardContent className="p-4 flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center flex-shrink-0">
                    <Wallet className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-slate-800">Earnings & Work</p>
                    <p className="text-xs text-slate-400">Shifts, pay and expenses</p>
                  </div>
                </CardContent>
              </Card>
            </Link>
            <Link to="/app/emergency-work" data-testid="quick-emergency-work">
              <Card className="bg-white border border-slate-200 hover:border-teal-200 hover:shadow-md transition-all cursor-pointer">
                <CardContent className="p-4 flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-amber-50 flex items-center justify-center flex-shrink-0">
                    <FolderOpen className="w-5 h-5 text-amber-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-slate-800">Emergency Work</p>
                    <p className="text-xs text-slate-400">Worksheets and resources</p>
                  </div>
                </CardContent>
              </Card>
            </Link>
          </div>
        </div>

        {/* Weekly Planner */}
        <Card className="bg-white border border-google-grey-200" data-testid="weekly-planner">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-lg font-heading font-medium text-google-grey-900">My Week</h2>
              <div className="flex items-center gap-2">
                <Button variant="ghost" size="sm" onClick={() => setWeekOffset(w => w - 1)} className="h-8 w-8 p-0 text-google-grey-500 hover:text-google-grey-800" data-testid="prev-week-btn">
                  <ChevronLeft className="w-5 h-5" />
                </Button>
                <button onClick={() => setWeekOffset(0)} className="text-sm font-medium text-google-grey-700 hover:text-google-blue px-2 py-1 rounded transition-colors" data-testid="current-week-btn">
                  {weekOffset === 0 ? 'This Week' : format(weekStart, 'MMM d') + ' – ' + format(addDays(weekStart, 4), 'MMM d')}
                </button>
                <Button variant="ghost" size="sm" onClick={() => setWeekOffset(w => w + 1)} className="h-8 w-8 p-0 text-google-grey-500 hover:text-google-grey-800" data-testid="next-week-btn">
                  <ChevronRight className="w-5 h-5" />
                </Button>
              </div>
            </div>

            <div className="grid grid-cols-5 gap-3">
              {weekDays.map((day, i) => {
                const dateStr = format(day, 'yyyy-MM-dd');
                const entry = plannerEntries[dateStr];
                const today = isToday(day);

                return (
                  <div
                    key={dateStr}
                    className={`rounded-xl border p-3 transition-all ${today ? 'ring-2 ring-google-blue border-google-blue bg-blue-50/50' : DAY_COLORS[i]}`}
                    data-testid={`planner-day-${dateStr}`}
                  >
                    <div className="text-center mb-2">
                      <p className={`text-xs font-medium ${today ? 'text-google-blue' : 'text-google-grey-500'}`}>
                        {DAY_LABELS[i]}
                      </p>
                      <p className={`text-lg font-bold ${today ? 'text-google-blue' : 'text-google-grey-800'}`}>
                        {format(day, 'd')}
                      </p>
                    </div>

                    {entry?.school_name ? (
                      <div className="bg-white rounded-lg p-2 border border-google-grey-200 group relative">
                        <p className="text-xs font-medium text-google-grey-800 text-center truncate pr-4" title={entry.school_name}>
                          {entry.school_name}
                        </p>
                        <button
                          onClick={() => handleSetPlannerDay(day, 'clear')}
                          className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 text-google-grey-400 hover:text-google-red transition-opacity"
                          data-testid={`clear-day-${dateStr}`}
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ) : (
                      <Select onValueChange={(val) => handleSetPlannerDay(day, val)}>
                        <SelectTrigger className="h-8 text-xs border-dashed border-google-grey-300 bg-white/60" data-testid={`select-school-${dateStr}`}>
                          <SelectValue placeholder="+ School" />
                        </SelectTrigger>
                        <SelectContent>
                          {schools.map(s => (
                            <SelectItem key={s.school_id} value={s.school_id} className="text-sm">{s.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      </main>

      {/* Edit School Dialog */}
      <Dialog open={!!editingSchool} onOpenChange={(open) => { if (!open) setEditingSchool(null); }}>
        <DialogContent className="sm:max-w-md" data-testid="edit-school-dialog">
          <DialogHeader>
            <DialogTitle>Edit School</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">School Name</label>
              <Input
                value={editSchoolName}
                onChange={(e) => setEditSchoolName(e.target.value)}
                placeholder="School name"
                className="rounded-xl"
                data-testid="edit-school-name-input"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">Address (optional)</label>
              <Input
                value={editSchoolAddress}
                onChange={(e) => setEditSchoolAddress(e.target.value)}
                placeholder="Address"
                className="rounded-xl"
                data-testid="edit-school-address-input"
              />
            </div>
            <Button
              onClick={handleRenameSchool}
              disabled={savingEdit || !editSchoolName.trim()}
              className="w-full bg-teal-600 hover:bg-teal-700 text-white rounded-xl h-11"
              data-testid="save-school-edit-btn"
            >
              {savingEdit ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
